// Invoices.js - Added Send Email functionality
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../../context/AuthContext";
import "./Invoices.scss";
import ReactDOM from "react-dom/client";
import {
  getInvoicesByID,
  getInvoicesCompany,
  getInvoicesCustomer,
  InvoicePaymentDetails,
  sendEmailByInvoiceID,
  updateInvoiceStatus,
} from "../../services/api/InvoicesApi";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { InvoicePDFTemplate } from "../InvoicePDFTemplate/InvoicePDFTemplate";
import { useSearchResults } from "../../context/SearchResultsContext";
import { useLoading } from "../../context/LoadingContext";
import ConfirmationModal from "../../components/Modal/ConfirmModal";

import { useToast } from "../../context/ToastContext";

import { FaEye } from "react-icons/fa";
import { IoGrid } from "react-icons/io5";
import { FaTableList } from "react-icons/fa6";
import gsap from "gsap";
import { useInvoiceSelection } from "../../context/InvoiceSelectionContext";
import { formatText } from "../../components/TruncateParties";

export const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoicePayments, setSelectedInvoicePayments] = useState([]);
  const { role } = useAuth();
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState("");
  const { showLoading, hideLoading } = useLoading();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoiceTotal, setSelectedInvoiceTotal] = useState(0);

  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  const {
    selectedCard: selectedInvoiceCard,
    handleCardClick: handleInvoiceCardClick,
    searchTerm: invoiceSearchTerm,
    setSearchTerm: setInvoiceSearchTerm,
    filters: invoiceFilters,
    setFilters: setInvoiceFilters,
    filteredInvoices,
    setFilteredInvoices,
    clearAllFilters: clearInvoiceFilters,
    hasActiveFilters: hasInvoiceFilters,
    refreshKey, // ADD THIS
    triggerInvoiceRefresh, // ADD THIS
    setIsLoading,
    isLoading,
    pagination,
    updatePaginationInfo,
    setPagination
  } = useInvoiceSelection();

  const [viewMode, setViewMode] = useState("table");

  // GSAP refs for checkbox animations
  const checkboxRefs = useRef([]);

  const { searchResults, searchType } = useSearchResults();
  const { globalSettings } = useGlobalSettings();
  const { showToast } = useToast();

  const [filters, setFilters] = useState({
    status: "",
    minAmount: "",
    maxAmount: "",
    fromDate: "",
    toDate: "",
    clientName: "",
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [cancelledRem, setCancelledRem] = useState("");

  const displayInvoices = useMemo(() => {
    console.log(
      "Using filteredInvoices from context:",
      filteredInvoices?.length || 0
    );
    return filteredInvoices || [];
  }, [filteredInvoices]);

  useEffect(() => {
    const fetchInvoicesWithFilters = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        // params.set("page", pagination.page.toString());
        // params.set("limit", pagination.limit.toString());

        // Add search term
        if (invoiceSearchTerm && invoiceSearchTerm.trim()) {
          params.set("search", invoiceSearchTerm.trim());
        }

        // Add card selection
        if (selectedInvoiceCard) {
          params.set("onClick", selectedInvoiceCard);
        }

        // Add filters with proper empty string checking
        Object.entries(invoiceFilters).forEach(([key, value]) => {
          if (value && typeof value === "string" && value.trim() !== "") {
            params.set(key, value.trim());
          }
        });

        // Add local UI filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value && typeof value === "string" && value.trim() !== "") {
            params.set(key, value.trim());
          }
        });

        const queryString = params.toString();
        console.log("Invoices component - Final query string:", queryString);

        const response =
          role === "customer"
            ? await getInvoicesCustomer(queryString ? `?${queryString}` : "")
            : await getInvoicesCompany(queryString ? `?${queryString}` : "");

        const invoiceData = response?.data || {};

        // ADD THIS LINE - Set the invoice data for stats
        if (window.setDetailSectionInvoiceData) {
          window.setDetailSectionInvoiceData(invoiceData);
        }

        setFilteredInvoices(invoiceData.invoice || []);
      } catch (err) {
        console.error("Error fetching filtered invoices:", err);
        setFilteredInvoices([]);
        updatePaginationInfo(0, false);
      } finally {
        setIsLoading(false);
      }
    };

    // Call API whenever filters, search term, or card selection changes
    const hasActiveFilters =
      selectedInvoiceCard !== null ||
      invoiceSearchTerm.trim() !== "" ||
      Object.values(invoiceFilters).some(
        (value) => value && value.trim() !== ""
      ) ||
      Object.values(filters).some((value) => value && value.trim() !== "");

    if (hasActiveFilters) {
      fetchInvoicesWithFilters();
    }
  }, [
    selectedInvoiceCard,
    invoiceSearchTerm,
    invoiceFilters,
    filters, // local UI filters
    role,
    refreshKey,
    pagination.page,
    pagination.limit,
    updatePaginationInfo
  ]);

  const isInSearchMode = searchType === "invoices";



    const handleInvoicePerPageChange = useCallback((e) => {
    const newLabelsPerPage = Number(e.target.value);
    setPagination(prev => ({
      ...prev,
      limit: newLabelsPerPage,
      page: 0
    }));
  }, [setPagination]);

  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  }, [setPagination]);

  // Reset to first page when filters change
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      page: 0
    }));
  }, [filters,setPagination])

  const calculatePendingAmount = (invoice) => {
    if (!invoice.payments || invoice.payments.length === 0) {
      return invoice.totalAmount;
    }

    const totalPaid = invoice.payments.reduce(
      (sum, payment) => sum + (payment.amount || 0),
      0
    );
    return Math.max(0, invoice.totalAmount - totalPaid);
  };

  const getNoDataMessage = () => {
    if (hasInvoiceFilters && displayInvoices.length === 0) {
      return "No invoices found matching your search and filters.";
    }

    if (searchType === "invoices" && displayInvoices.length === 0) {
      return "No invoices found matching your search.";
    }

    return "No invoices found. Start by creating a new one!";
  };

  const shouldShowNoDataMessage = useMemo(() => {
    return displayInvoices.length === 0;
  }, [displayInvoices.length]);

  const handleSendEmail = async () => {
    if (!showCheckboxes) {
      setShowCheckboxes(true);
      // Use setTimeout to ensure DOM is updated before animating
      setTimeout(() => {
        animateCheckboxesIn();
      }, 50);
      return;
    }

    if (selectedInvoices.length === 0) {
      showToast("Please select at least one invoice", "error");
      return;
    }

    try {
      showLoading("Sending emails...");
      const payload = { invoiceIds: selectedInvoices };

      const response = await sendEmailByInvoiceID(payload);
      console.log("API Response dnkjsnvka:", response);

      // Check if emailConfig exists and is false
      if (response?.data?.emailConfig === false) {
        showToast(
          "Fill all fields for invoice email configuration in global settings",
          "error"
        );
        return response;
      }

      // If emailConfig doesn't exist or is true, show success
      showToast(`Emails sent successfully for selected invoices!`, "success");

      // Animate checkboxes out before hiding
      animateCheckboxesOut();
      setTimeout(() => {
        setShowCheckboxes(false);
      }, 400); // Wait for animation to complete

      // Return the response if needed by the calling function
      return response;
    } catch (error) {
      console.error("Error sending emails:", error);
      showToast(`Failed to send emails: ${error.message}`, "error");
    } finally {
      hideLoading();
    }
  };
  const handleCheckboxChange = (id, event) => {
    if (event?.target) {
      animateCheckboxClick(event.target);
    }

    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((invId) => invId !== id) : [...prev, id]
    );
  };

  // GSAP Animation for showing checkboxes
  const animateCheckboxesIn = () => {
    const checkboxes = checkboxRefs.current.filter((ref) => ref !== null);

    if (checkboxes.length === 0) return;

    // Initial state - hide all checkboxes
    gsap.set(checkboxes, {
      opacity: 0,
      scale: 0.8,
      transformOrigin: "center center",
    });

    // Simple fade in with scale animation
    gsap.to(checkboxes, {
      duration: 0.4,
      opacity: 1,
      scale: 1,
      ease: "power2.out",
      stagger: {
        amount: 0.2,
        from: "start",
      },
    });
  };

  // GSAP Animation for hiding checkboxes
  const animateCheckboxesOut = () => {
    const checkboxes = checkboxRefs.current.filter((ref) => ref !== null);

    if (checkboxes.length === 0) return;

    const tl = gsap.timeline({
      onComplete: () => {
        setSelectedInvoices([]);
        checkboxRefs.current = [];
      },
    });

    // Simple fade out with scale animation
    tl.to(checkboxes, {
      duration: 0.3,
      opacity: 0,
      scale: 0.8,
      ease: "power2.in",
      stagger: {
        amount: 0.15,
        from: "end",
      },
    });
  };

  // Animation for individual checkbox interactions
  const animateCheckboxClick = (checkboxElement) => {
    // Simple scale animation on click
    gsap.to(checkboxElement, {
      duration: 0.15,
      scale: 0.9,
      ease: "power2.out",
      yoyo: true,
      repeat: 1,
    });
  };

  const handlePreview = async (id) => {
  try {
    showLoading("Loading...");
    console.log("Starting PDF preview for invoice:", id);

    const response = await getInvoicesByID(id);
    const invoice = response.data;
    const invoiceSettings = globalSettings?.result?.[0]?.invoiceSettings;

    console.log("Invoice data:", invoice);
    console.log("Settings data:", invoiceSettings);

    const container = document.createElement("div");
    container.id = "pdf-preview-container";
    container.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 794px;
      min-height: auto;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: 9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

    document.body.appendChild(container);

    // Create root and render
    const root = ReactDOM.createRoot(container);

    // Render the component
    root.render(
      <div
        style={{
          width: "794px",
          minHeight: "auto",
          backgroundColor: "white",
          display: "block",
          margin: 0,
          padding: 0,
        }}
      >
        <InvoicePDFTemplate invoice={invoice} settings={invoiceSettings} />
      </div>
    );

    console.log("Component rendered, waiting for content to load...");

    // Wait for React to render and calculate actual height
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Wait for all images to load
    const allImages = container.querySelectorAll("img");
    console.log("Found images:", allImages.length);

    if (allImages.length > 0) {
      await Promise.all(
        Array.from(allImages).map((img, index) => {
          return new Promise((resolve) => {
            console.log(`Checking image ${index + 1}:`, img.src);

            if (img.complete && img.naturalWidth > 0) {
              console.log(`Image ${index + 1} already loaded`);
              resolve();
            } else {
              const timeout = setTimeout(() => {
                console.warn(`Image ${index + 1} load timeout:`, img.src);
                resolve();
              }, 8000);

              img.onload = () => {
                console.log(`Image ${index + 1} loaded successfully`);
                clearTimeout(timeout);
                resolve();
              };

              img.onerror = () => {
                console.error(`Image ${index + 1} failed to load:`, img.src);
                clearTimeout(timeout);
                resolve();
              };
            }
          });
        })
      );
    }

    // Get the actual content height after rendering
    const actualHeight = Math.max(
      container.scrollHeight,
      container.offsetHeight,
      1123
    );

    // Update container with actual dimensions - position in viewport but behind everything
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 794px;
      height: ${actualHeight}px;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: -9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    console.log("Content loaded, generating PDF for preview...");

    if (!container.innerHTML.trim()) {
      throw new Error("Container is empty - no content to convert to PDF");
    }

    const pdfWidth = 8.27;
    const pdfHeight = Math.max(11.69, actualHeight / 96);

    const options = {
      margin: 0,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: true,
        width: 794,
        height: actualHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 794,
        windowHeight: actualHeight,
      },
      jsPDF: {
        unit: "in",
        format: [pdfWidth, pdfHeight],
        orientation: "portrait",
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    const html2pdf = (await import("html2pdf.js")).default;

    // Generate PDF as blob instead of downloading
    const pdfBlob = await html2pdf()
      .set(options)
      .from(container)
      .outputPdf("blob");

    // Create blob URL and open in new tab
    const blobUrl = URL.createObjectURL(pdfBlob);
    const previewWindow = window.open(blobUrl, "_blank");

    if (!previewWindow) {
      showToast("Please allow popups for preview functionality", "error");
      URL.revokeObjectURL(blobUrl);
      return;
    }

    console.log("PDF preview opened successfully!");

    // Clean up the blob URL after some time
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 60000); // Clean up after 1 minute

    // Clean up DOM
    setTimeout(() => {
      try {
        root.unmount();
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      } catch (cleanupError) {
        console.warn("Cleanup error:", cleanupError);
      }
    }, 1000);
  } catch (error) {
    console.error("Error generating PDF preview:", error);
    showToast(`Failed to generate preview: ${error.message}`, "error");

    const container = document.getElementById("pdf-preview-container");
    if (container && document.body.contains(container)) {
      try {
        document.body.removeChild(container);
      } catch (cleanupError) {
        console.warn("Cleanup error:", cleanupError);
      }
    }
  } finally {
    hideLoading();
  }
};

const handleDownload = async (id) => {
  try {
    showLoading("Loading...");
    console.log("Starting PDF generation for invoice:", id);

    const response = await getInvoicesByID(id);
    const invoice = response.data;
    const invoiceSettings = globalSettings?.result?.[0]?.invoiceSettings;

    console.log("Invoice data:", invoice);
    console.log("Settings data:", invoiceSettings);

    const container = document.createElement("div");
    container.id = "pdf-preview-container";
    container.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 794px;
      min-height: auto;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: 9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

    document.body.appendChild(container);

    // Create root and render
    const root = ReactDOM.createRoot(container);

    // Render the component
    root.render(
      <div
        style={{
          width: "794px",
          minHeight: "auto",
          backgroundColor: "white",
          display: "block",
          margin: 0,
          padding: 0,
        }}
      >
        <InvoicePDFTemplate invoice={invoice} settings={invoiceSettings} />
      </div>
    );

    console.log("Component rendered, waiting for content to load...");

    // Wait for React to render and calculate actual height
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Wait for all images to load
    const allImages = container.querySelectorAll("img");
    console.log("Found images:", allImages.length);

    if (allImages.length > 0) {
      await Promise.all(
        Array.from(allImages).map((img, index) => {
          return new Promise((resolve) => {
            console.log(`Checking image ${index + 1}:`, img.src);

            if (img.complete && img.naturalWidth > 0) {
              console.log(`Image ${index + 1} already loaded`);
              resolve();
            } else {
              const timeout = setTimeout(() => {
                console.warn(`Image ${index + 1} load timeout:`, img.src);
                resolve();
              }, 8000);

              img.onload = () => {
                console.log(`Image ${index + 1} loaded successfully`);
                clearTimeout(timeout);
                resolve();
              };

              img.onerror = () => {
                console.error(`Image ${index + 1} failed to load:`, img.src);
                clearTimeout(timeout);
                resolve();
              };
            }
          });
        })
      );
    }

    // Get the actual content height after rendering
    const actualHeight = Math.max(
      container.scrollHeight,
      container.offsetHeight,
      1123
    );

    // Update container with actual dimensions - position in viewport but behind everything
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 794px;
      height: ${actualHeight}px;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: -9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    console.log("Content loaded, generating PDF...");
    console.log(
      "Container dimensions:",
      container.offsetWidth,
      "x",
      actualHeight
    );
    console.log("Scroll height:", container.scrollHeight);

    if (!container.innerHTML.trim()) {
      throw new Error("Container is empty - no content to convert to PDF");
    }

    const pdfWidth = 8.27;
    const pdfHeight = Math.max(11.69, actualHeight / 96);

    const options = {
      margin: 0,
      filename: `${invoice.invoiceNumber || "invoice"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: true,
        width: 794,
        height: actualHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 794,
        windowHeight: actualHeight,
      },
      jsPDF: {
        unit: "in",
        format: [pdfWidth, pdfHeight],
        orientation: "portrait",
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    const html2pdf = (await import("html2pdf.js")).default;

    await html2pdf().set(options).from(container).save();

    console.log("PDF generated successfully!");

    // Clean up
    setTimeout(() => {
      try {
        root.unmount();
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      } catch (cleanupError) {
        console.warn("Cleanup error:", cleanupError);
      }
    }, 1000);
  } catch (error) {
    console.error("Error generating invoice PDF:", error);
    showToast(`Failed to generate PDF: ${error.message}`, "error");

    const container = document.getElementById("pdf-preview-container");
    if (container && document.body.contains(container)) {
      try {
        document.body.removeChild(container);
      } catch (cleanupError) {
        console.warn("Cleanup error:", cleanupError);
      }
    }
  } finally {
    hideLoading();
  }
};

  // useEffect(() => {
  //   const fetchInitialInvoices = async () => {
  //     // Only fetch initial data if we don't have any filtered invoices yet
  //     if (filteredInvoices.length > 0) {
  //       console.log('Using existing filtered invoices from context');
  //       setInvoices(filteredInvoices);
  //       return;
  //     }

  //     try {
  //       showLoading("Loading");
  //       const response = role === "customer"
  //         ? await getInvoicesCustomer()
  //         : await getInvoicesCompany();

  //       console.log("Initial invoice fetch:", response.data.invoice?.length || 0);
  //       const invoiceData = response.data.invoice || [];

  //       setInvoices(invoiceData);
  //       // Set initial filtered invoices in context only if no filters are active
  //       if (!hasInvoiceFilters) {
  //         setFilteredInvoices(invoiceData);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching invoices:", error);
  //       showToast("Failed to fetch invoices. Please try again.", "error");
  //     } finally {
  //       hideLoading();
  //     }
  //   };

  //   fetchInitialInvoices();
  // }, [role]);

  // REPLACE your existing handleStatusUpdateClick function with this fixed version:

  // TEMPORARILY replace your handleStatusUpdateClick function with this debug version:
  const handleStatusUpdateClick = async (id) => {
    try {
      showLoading("Loading invoice details...");
      console.log(id, "fsbvhjfs");

      // Always fetch fresh invoice data from API instead of using stale data from state
      const response = await getInvoicesByID(id);
      const invoice = response.data;

      console.log("=== DEBUG: Full API Response ===");
      console.log("Full response:", response);
      console.log("Response data:", response.data);
      console.log("Invoice object:", invoice);
      console.log("Invoice keys:", Object.keys(invoice || {}));
      console.log("=== END DEBUG ===");

      setSelectedInvoiceId(id);

      // Check if invoice exists
      if (!invoice) {
        console.error("No invoice data found in response");
        showToast("No invoice data found. Please try again.", "error");
        return;
      }

      // Log all potential amount fields
      console.log("=== AMOUNT DEBUGGING ===");
      console.log(
        "invoice.totalAmount:",
        invoice.totalAmount,
        typeof invoice.totalAmount
      );
      console.log("invoice.total:", invoice.total, typeof invoice.total);
      console.log("invoice.amount:", invoice.amount, typeof invoice.amount);
      console.log("invoice.items:", invoice.items);
      console.log("cancelInvoiceRemark", invoice?.cancelInvoiceRemark);
      if (invoice?.cancelInvoiceRemark) {
        setCancelledRem(invoice.cancelInvoiceRemark);
      } else {
        setCancelledRem(""); // Clear any previous remark
      }
      if (invoice.items) {
        console.log("Items array length:", invoice.items.length);
        invoice.items.forEach((item, index) => {
          console.log(`Item ${index}:`, item);
        });
      }
      console.log("=== END AMOUNT DEBUG ===");

      // For now, let's bypass the amount validation and see what happens
      let invoiceTotal = 0;

      // Try to find the total amount
      if (invoice.totalAmount !== undefined && invoice.totalAmount !== null) {
        invoiceTotal = Number(invoice.totalAmount);
        console.log("Using totalAmount:", invoiceTotal);
      } else if (invoice.total !== undefined && invoice.total !== null) {
        invoiceTotal = Number(invoice.total);
        console.log("Using total:", invoiceTotal);
      } else if (invoice.amount !== undefined && invoice.amount !== null) {
        invoiceTotal = Number(invoice.amount);
        console.log("Using amount:", invoiceTotal);
      } else if (
        invoice.items &&
        Array.isArray(invoice.items) &&
        invoice.items.length > 0
      ) {
        invoiceTotal = invoice.items.reduce((sum, item) => {
          const itemAmount = Number(item.amount) || 0;
          console.log(`Adding item amount: ${itemAmount}`);
          return sum + itemAmount;
        }, 0);
        console.log("Calculated from items:", invoiceTotal);
      } else {
        console.log("No amount fields found, defaulting to 0");
        invoiceTotal = 0;
      }

      console.log("Final calculated total:", invoiceTotal);

      // TEMPORARILY: Let's proceed even with 0 amount to see the modal
      // if (invoiceTotal === 0) {
      //   console.error("❌ Could not determine invoice total amount!");
      //   showToast(
      //     "Unable to determine invoice total amount. Please check the invoice data.",
      //     "error"
      //   );
      //   return;
      // }

      const currentInvoiceStatus = invoice?.status || "pending";
      console.log("Current invoice status from API:", currentInvoiceStatus);

      // If status is cancelled, show modal with limited options
      if (currentInvoiceStatus === "cancelled") {
        setSelectedInvoiceStatus("cancelled");
        setSelectedInvoiceTotal(invoiceTotal);
        setSelectedInvoicePayments([]); // No payments for cancelled invoices
        setStatusModalOpen(true);
        return;
      }

      // For non-cancelled invoices, calculate status based on payments
      let calculatedStatus = currentInvoiceStatus;
      if (invoice?.payments && invoice.payments.length > 0) {
        const totalPaid = invoice.payments.reduce(
          (sum, p) => sum + (Number(p.amount) || 0),
          0
        );
        if (totalPaid >= invoiceTotal) {
          calculatedStatus = "paid";
        } else {
          calculatedStatus = "part-payment";
        }
      }

      setSelectedInvoiceStatus(calculatedStatus);
      setSelectedInvoiceTotal(invoiceTotal);
      setSelectedInvoicePayments(invoice?.payments || []);
      setStatusModalOpen(true);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      showToast("Failed to load invoice details. Please try again.", "error");
    } finally {
      hideLoading();
    }
  };

  const handleFilterChange = useCallback(
    (field, value) => {
      console.log(
        "Filter changed:",
        field,
        "from",
        filters[field],
        "to",
        value
      );
      setIsLoading(true);

      // Always update the filter, even if it's an empty string
      const newFilters = { ...filters, [field]: value || "" };
      console.log("New filters object:", newFilters);
      setFilters(newFilters);
    },
    [filters, setFilters, setIsLoading]
  );

  const getStatusOptions = () => {
    // If current status is cancelled, only allow viewing (no changes)
    if (selectedInvoiceStatus === "cancelled") {
      return [{ label: "Cancelled", value: "cancelled" }];
    }

    const baseOptions = [
      { label: "Pending", value: "pending" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Part-Payment", value: "part-payment" },
    ];

    // Only show "Paid" option if there are no existing payments
    if (!selectedInvoicePayments || selectedInvoicePayments.length === 0) {
      baseOptions.unshift({ label: "Paid", value: "paid" });
    }

    return baseOptions;
  };

  // REPLACE your existing handleConfirmStatusUpdate function:
  const handleConfirmStatusUpdate = async (
    newStatus,
    cancellationRemark = ""
  ) => {
    if (newStatus === "paid" || newStatus === "part-payment") {
      setStatusModalOpen(false);
      setSelectedInvoiceId(null);
      showToast("Payment details updated.", "success");

      // FIXED: Always refresh invoices after payment updates
      await refreshInvoiceList();
      return;
    }

    try {
      showLoading("Updating status...");

      // Handle cancellation with optional remark
      if (newStatus === "cancelled") {
        console.log(
          "Updating invoice status to cancelled with remark:",
          cancellationRemark
        );
        await updateInvoiceStatus(
          selectedInvoiceId,
          newStatus,
          cancellationRemark
        );
      } else {
        // For other status updates (no remark needed)
        await updateInvoiceStatus(selectedInvoiceId, newStatus);
      }

      setStatusModalOpen(false);
      setSelectedInvoiceId(null);
      showToast("Invoice status updated!", "success");

      // FIXED: Always refresh invoices after status updates
      await refreshInvoiceList();
    } catch (err) {
      console.error("Failed to update status:", err);
      showToast("Failed to update invoice status.", "error");
    } finally {
      hideLoading();
    }
  };

  const refreshInvoiceList = async () => {
    try {
      showLoading("Refreshing invoices...");

      // Call the same API endpoint with current filters to get fresh data
      const params = new URLSearchParams();
      params.set("page", "0");
      params.set("limit", "20");

      // Add current search term
      if (invoiceSearchTerm && invoiceSearchTerm.trim()) {
        params.set("search", invoiceSearchTerm.trim());
      }

      // Add current card selection
      if (selectedInvoiceCard) {
        params.set("onClick", selectedInvoiceCard);
      }

      // Add current filters
      Object.entries(invoiceFilters).forEach(([key, value]) => {
        if (value && typeof value === "string" && value.trim() !== "") {
          params.set(key, value.trim());
        }
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value && typeof value === "string" && value.trim() !== "") {
          params.set(key, value.trim());
        }
      });

      const queryString = params.toString();
      console.log("Refreshing invoices with query:", queryString);

      const response =
        role === "customer"
          ? await getInvoicesCustomer(queryString ? `?${queryString}` : "")
          : await getInvoicesCompany(queryString ? `?${queryString}` : "");

      const invoiceData = response?.data || {};

      // Update the invoice data for stats
      if (window.setDetailSectionInvoiceData) {
        window.setDetailSectionInvoiceData(invoiceData);
      }

      // Update filtered invoices in context
      setFilteredInvoices(invoiceData.invoice || []);

      // Trigger refresh in context to update other components
      triggerInvoiceRefresh();

      console.log("✅ Invoices refreshed successfully");
    } catch (error) {
      console.error("Error refreshing invoices:", error);
      showToast("Failed to refresh invoice list.", "error");
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCancelStatusUpdate = () => {
    setStatusModalOpen(false);
    setSelectedInvoiceId(null);
  };

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownOverflow, setDropdownOverflow] = useState("auto");

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      // Always show below the button, but limit maxHeight if needed
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    const handleScroll = (e) => {
      if (!isFilterOpen) return;
      // Only close if scroll is outside the dropdown
      const dropdown = document.querySelector(".filter-dropdown-box");
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        // Scrolling inside the dropdown, do nothing
        return;
      }
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isFilterOpen]);

  // Check if dropdown content is overflowing and set overflowY accordingly
  useEffect(() => {
    if (isFilterOpen && filterDropdownRef.current) {
      const el = filterDropdownRef.current;
      if (el.scrollHeight > el.clientHeight) {
        setDropdownOverflow("auto");
      } else {
        setDropdownOverflow("visible");
      }
    }
  }, [isFilterOpen, dropdownPosition]);

  // Cleanup GSAP animations on component unmount
  useEffect(() => {
    return () => {
      gsap.killTweensOf(checkboxRefs.current);
    };
  }, []);

  // Reset refs when invoices change
  useEffect(() => {
    checkboxRefs.current = [];
  }, [filteredInvoices]);

  useEffect(() => {
  // Expose function to clear local filters from external components
  window.clearInvoiceFilters = () => {
    setFilters({
      status: "",
      minAmount: "",
      maxAmount: "",
      fromDate: "",
      toDate: "",
      clientName: "",
    });
  };
  
  return () => {
    delete window.clearInvoiceFilters;
  };
}, []);

function formatNumberAbbr(num) {
    // Handle undefined, null, or invalid numbers
    if (num === undefined || num === null || num === "") return "0";
    if (typeof num !== "number") num = Number(num);
    if (isNaN(num)) return "0";
    if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
    return num.toString();
  }

  return (
    <div className={`invoices-container`}>
      {/* <h2>{role === "customer" ? "Your Invoices" : "Company Invoices"}</h2> */}
      <div className="invoices-header">
        <div className="view-toggle-wrapper">
          <button
            className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewMode("grid")}
          >
            <IoGrid />
          </button>
          <button
            className={`view-toggle-btn ${
              viewMode === "table" ? "active" : ""
            }`}
            onClick={() => setViewMode("table")}
          >
            <FaTableList />
          </button>
            <div className="common-pagination-settings">
              <label htmlFor="invoicePerPageSelect">Invoices per page:</label>
              <select
                id="invoicePerPageSelect"
                value={pagination.limit}
                onChange={handleInvoicePerPageChange}
              >
                {[5, 10, 15, 20, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
        </div>

        <div className="header-buttons">
          {role !== "customer" && (
            <button className="send-email-btn" onClick={handleSendEmail}>
              {showCheckboxes ? "Send Selected" : "Send Email"}
            </button>
          )}
          {showCheckboxes && (
            <button
              className="cancel-select-btn"
              onClick={() => {
                animateCheckboxesOut();
                setTimeout(() => {
                  setShowCheckboxes(false);
                }, 400); // Wait for animation to complete
              }}
            >
              Cancel
            </button>
          )}
          <div className="filter-dropdown-wrapper">
            <button
              className="filter-toggle-btn"
              onClick={() => setIsFilterOpen((prev) => !prev)}
              ref={filterButtonRef}
            >
              {isFilterOpen ? "Hide Filters" : "Show Filters"}
            </button>

            {isFilterOpen &&
              dropdownPosition.top !== 0 &&
              dropdownPosition.left !== 0 && (
                <div
                  className="filter-dropdown-box"
                  ref={filterDropdownRef}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    maxHeight: dropdownPosition.maxHeight
                      ? `${dropdownPosition.maxHeight}px`
                      : "500px",
                    overflowY: dropdownOverflow,
                    position: "fixed",
                  }}
                >
                  <div className="filter-group">
                    <label>Status:</label>
                    <select
                      value={filters.status}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                    >
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="part-payment">Part-Payment</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  {role !== "customer" && (
                    <div className="filter-group">
                      <label>Client Name:</label>
                      <input
                        type="text"
                        placeholder="Enter client name"
                        value={filters.clientName}
                        onChange={(e) =>
                          handleFilterChange("clientName", e.target.value)
                        }
                      />
                    </div>
                  )}

                  <div className="filter-group">
                    <label>Min Total:</label>
                    <input
                      type="number"
                      placeholder="₹ Min"
                      value={filters.minAmount}
                      onChange={(e) =>
                        handleFilterChange("minAmount", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Max Total:</label>
                    <input
                      type="number"
                      placeholder="₹ Max"
                      value={filters.maxAmount}
                      onChange={(e) =>
                        handleFilterChange("maxAmount", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>From Date:</label>
                    <input
                      type="date"
                      value={filters.fromDate}
                      onChange={(e) =>
                        handleFilterChange("fromDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>To Date:</label>
                    <input
                      type="date"
                      value={filters.toDate}
                      onChange={(e) =>
                        handleFilterChange("toDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-actions">
                    <button
                      className="clear-btn"
                onClick={() => {
                  clearInvoiceFilters();
                  setFilters({
                    status: "",
                    minAmount: "",
                    maxAmount: "",
                    fromDate: "",
                    toDate: "",
                    clientName: "",
                  }); 
                }}
              >
                      Clear
                    </button>
                    <button
                      className="hide-btn"
                      onClick={() => setIsFilterOpen(false)}
                    >
                      Hide
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
      {(hasInvoiceFilters || Object.values(filters).some((val) => val)) && (
        <div className="applied-filters">
          <span className="filters-text">Applied Filters:</span>

          {/* Search term filter pill */}
          {invoiceSearchTerm && invoiceSearchTerm.trim() && (
            <span
              className="filter-tag"
              onClick={() => {
                setInvoiceSearchTerm("");
                // Clear DetailSection's search input
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
              }}
            >
              Search: {formatText(invoiceSearchTerm)} ×
            </span>
          )}

          {/* Card selection filter pill */}
          {selectedInvoiceCard && (
            <span
              className="filter-tag"
              onClick={() => handleInvoiceCardClick(selectedInvoiceCard)}
            >
              Card: {formatText(selectedInvoiceCard)} ×
            </span>
          )}

          {/* Individual local filter pills */}
          {filters.status && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, status: "" })}
            >
              Status: {formatText(filters.status)} ×
            </span>
          )}

          {filters.clientName && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, clientName: "" })}
            >
              Client Name: {formatText(filters.clientName)} ×
            </span>
          )}

          {filters.minAmount && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, minAmount: "" })}
            >
              Min: ₹{formatText(filters.minAmount)} ×
            </span>
          )}

          {filters.maxAmount && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, maxAmount: "" })}
            >
              Max: ₹{formatText(filters.maxAmount)} ×
            </span>
          )}

          {filters.fromDate && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, fromDate: "" })}
            >
              Start Date: {formatText(filters.fromDate)} ×
            </span>
          )}

          {filters.toDate && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, toDate: "" })}
            >
              End Date: {formatText(filters.toDate)} ×
            </span>
          )}

          <span
            className="filter-tag clear-all-tag"
            onClick={() => {
              clearInvoiceFilters();
              // Clear local filters
              setFilters({
                status: "",
                minAmount: "",
                maxAmount: "",
                fromDate: "",
                toDate: "",
                clientName: "",
              });
              // Clear DetailSection's search input
              if (window.clearDetailSectionSearch) {
                window.clearDetailSectionSearch();
              }
              handleInvoiceCardClick(null);

            }}
          >
            Clear All
          </span>
        </div>
      )}

      {shouldShowNoDataMessage ? (
        <p className="no-invoice-found">{getNoDataMessage()}</p>
      ) : (
        <>
          {/* {(filters.status || filters.minAmount || filters.maxAmount || hasInvoiceFilters) && (
              <div className="applied-filters">
                <strong>Applied Filters:</strong>
                {selectedInvoiceCard && <span>Card: {selectedInvoiceCard}</span>}
                {invoiceSearchTerm && <span>Search: {invoiceSearchTerm}</span>}
                {filters.status && <span>Status: {filters.status}</span>}
                {filters.minAmount && <span>Min: ₹{filters.minAmount}</span>}
                {filters.maxAmount && <span>Max: ₹{filters.maxAmount}</span>}
                {filters.fromDate && <span>Start Date: {filters.fromDate}</span>}
                {filters.toDate && <span>End Date: {filters.toDate}</span>}
                {filters.clientName && <span>Client Name: {filters.clientName}</span>}
              </div>
            )} */}

          {viewMode === "grid" ? (
            <div className="invoice-grid">
              {displayInvoices.map((inv, index) => (
                <div 
                  className="invoice-card" 
                  key={inv._id}
                  onClick={(e) => {
                    if (showCheckboxes) {
                      handleCheckboxChange(inv._id, e);
                    }
                  }}
                  style={{ cursor: showCheckboxes ? 'pointer' : 'default' }}
                >
                  <div className="header">
                    <h4>{inv.invoiceNumber}</h4>
                    {showCheckboxes && (
                      <input
                        type="checkbox"
                        ref={(el) => (checkboxRefs.current[index] = el)}
                        className="checkbox-fade"
                        checked={selectedInvoices.includes(inv._id)}
                        onChange={(e) => handleCheckboxChange(inv._id, e)}
                      />
                    )}
                    <span className={`status ${inv.status}`}>{inv.status}</span>
                  </div>
                  {role !== "customer" && (
                    <div className="updating-status">
                      <button
                        type="button"
                        className="status-update-btn"
                        onClick={() => handleStatusUpdateClick(inv._id)}
                      >
                        Update Status
                      </button>
                    </div>
                  )}
                  {/* <p>
                      <strong>Total:</strong> ₹{inv.totalAmount}
                    </p> */}
                  <div className="amount-dropdown">
                    <div className="amount-header">
                      <strong>Amount Details</strong>
                      <span className="dropdown-arrow">▼</span>
                    </div>
                    <div className="amount-details">
                      <div className="amount-row">
                        <span>Total:</span>
                        <span>₹{formatNumberAbbr(inv.totalAmount)}</span>
                      </div>
                      <div className="amount-row pending">
                        <span>Pending:</span>
                        <span>₹{formatNumberAbbr(calculatePendingAmount(inv))}</span>
                      </div>
                    </div>
                  </div>
                  <p>
                    <strong>Issued To:</strong> {inv.clientName}
                  </p>
                  {/* {inv.clientName && <p><strong>Client Name:</strong> {inv?.clientName}</p>} */}
                  <p>
                    <strong>Date:</strong>{" "}
                    {new Date(inv.createdAt).toLocaleDateString()}
                  </p>

                  <div className="items">
                    <strong>Items:</strong>
                    {inv.items.map((item, i) => (
                      <div className="item" key={i}>
                        <div>{item.description}</div>
                        <div>Qty: {item.qty}</div>
                        <div>₹{item.amount}</div>
                      </div>
                    ))}
                  </div>

                  {/* <div className="invoice-actions">
                      <button
                        type="button"
                        onClick={() => handlePreview(inv._id)}
                        disabled={
                          inv.status === "cancelled" ||
                          inv.status === "pending" ||
                          inv.status === "part-payment"
                        }
                        className={`preview-btn ${inv.status === "cancelled" ||
                            inv.status === "pending" ||
                            inv.status === "part-payment"
                            ? "disabled-btn"
                            : ""
                          }`}
                      >
                        <FaEye /> Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(inv._id)}
                        disabled={
                          inv.status === "cancelled" ||
                          inv.status === "pending" ||
                          inv.status === "part-payment"
                        }
                        className={`download-btn ${inv.status === "cancelled" ||
                            inv.status === "pending" ||
                            inv.status === "part-payment"
                            ? "disabled-btn"
                            : ""
                          }`}
                      >
                        Download
                      </button>
                    </div> */}

                  <div className="invoice-actions">
                    <button
                      type="button"
                      onClick={() => handlePreview(inv._id)}
                      className="preview-btn"
                    >
                      <FaEye /> Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(inv._id)}
                      className="download-btn"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className="invoice-table">
              <thead>
                <tr>
                  {showCheckboxes && <th>Select</th>}
                  <th>Invoice No</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {displayInvoices.map((inv, index) => (
                  <tr 
                    key={inv._id}
                    onClick={(e) => {
                      if (showCheckboxes) {
                        handleCheckboxChange(inv._id, e);
                      }
                    }}
                    style={{ cursor: showCheckboxes ? 'pointer' : 'default' }}
                  >
                    {showCheckboxes && (
                      <td>
                        <input
                          type="checkbox"
                          ref={(el) => (checkboxRefs.current[index] = el)}
                          className="checkbox-fade"
                          checked={selectedInvoices.includes(inv._id)}
                          onChange={(e) => handleCheckboxChange(inv._id, e)}
                        />
                      </td>
                    )}
                    <td>{inv.invoiceNumber}</td>
                    <td>
                      <span className={`status ${inv.status}`}>
                        {inv.status}
                      </span>
                    </td>
                    {/* <td>₹{inv.totalAmount}</td> */}
                    <td>
                      <div className="amount-dropdown table-dropdown">
                        <div className="amount-header">
                          <span>₹{formatNumberAbbr(inv.totalAmount)}</span>
                          <span className="dropdown-arrow">▼</span>
                        </div>
                        <div className="amount-details">
                          <div className="amount-row">
                            <span>Total:</span>
                            <span>₹{formatNumberAbbr(inv.totalAmount)}</span>
                          </div>
                          <div className="amount-row pending">
                            <span>Pending:</span>
                            <span>₹{formatNumberAbbr(calculatePendingAmount(inv))}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{inv.clientName}</td>
                    <td>{new Date(inv.createdAt).toLocaleDateString()}</td>
                    <td>
                      {inv.items.map((item, idx) => (
                        <div key={idx}>
                          <ul className="item-list-square">
                            <li>
                              {" "}
                              {item.description} (Qty: {item.qty}, ₹
                              {item.amount})
                            </li>
                          </ul>
                        </div>
                      ))}
                    </td>
                    {/* <td>
                        {role !== "customer" && (
                          <button
                            onClick={() => handleStatusUpdateClick(inv._id)}
                          >
                            Update Status
                          </button>
                        )}
                        <button
                          onClick={() => handlePreview(inv._id)}
                          disabled={
                            inv.status === "cancelled" ||
                            inv.status === "pending" ||
                            inv.status === "part-payment"
                          }
                          className="preview-btn"
                        >
                          <FaEye /> Preview
                        </button>
                        <button
                          onClick={() => handleDownload(inv._id)}
                          disabled={
                            inv.status === "cancelled" ||
                            inv.status === "pending" ||
                            inv.status === "part-payment"
                          }
                        >
                          Download
                        </button>
                      </td> */}
                    <td>
                      {role !== "customer" && (
                        <button
                          onClick={() => handleStatusUpdateClick(inv._id)}
                        >
                          Update Status
                        </button>
                      )}
                      <button
                        onClick={() => handlePreview(inv._id)}
                        className="preview-btn"
                      >
                        <FaEye /> Preview
                      </button>
                      <button onClick={() => handleDownload(inv._id)}>
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}



      <ConfirmationModal
        isOpen={statusModalOpen}
        invoiceId={selectedInvoiceId}
        message="Update Invoice Status"
        options={getStatusOptions()}
        currentStatus={selectedInvoiceStatus}
        totalAmount={selectedInvoiceTotal}
        previousPayments={selectedInvoicePayments}
        submitPaymentDetails={async (id, payments) => {
          const payload = { payments };
          console.log(
            selectedInvoicePayments,
            "console.log(selectedInvoicePayments)"
          );
          console.log("Submitting Payment Payload:", payload);

          await InvoicePaymentDetails(id, payload);
        }}
        onConfirm={handleConfirmStatusUpdate}
        onCancel={handleCancelStatusUpdate}
        activePage="Invoice"
        cancelledRem={cancelledRem}
      />

              {(pagination.totalInvoices > 0 && (pagination.totalInvoices > pagination.limit || pagination.page > 0)) && (
          <div className="pagination-controls">
            <button
              disabled={pagination.page === 0}
              onClick={() => handlePageChange(0)}
            >
              First
            </button>
            <button
              disabled={pagination.page === 0}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <span>
              Page {pagination.page + 1} of {Math.ceil(pagination.totalInvoices / pagination.limit)}
            </span>
            <button
              disabled={pagination.page >= Math.ceil(pagination.totalInvoices / pagination.limit) - 1}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
            <button
              disabled={pagination.page >= Math.ceil(pagination.totalInvoices / pagination.limit) - 1}
              onClick={() => handlePageChange(Math.ceil(pagination.totalInvoices / pagination.limit) - 1)}
            >
              Last
            </button>
          </div>
        )}
    </div>
  );
};